import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import { DescriptorCategory } from '@/types';

interface DescriptorPickerProps {
  categories: DescriptorCategory[];
  selected: string[];
  onChange: (selected: string[]) => void;
  maxSelections?: number;
}

export default function DescriptorPicker({
  categories,
  selected,
  onChange,
  maxSelections = 10,
}: DescriptorPickerProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(
    categories[0]?.name || null
  );

  const toggleDescriptor = (descriptor: string) => {
    if (selected.includes(descriptor)) {
      onChange(selected.filter((d) => d !== descriptor));
    } else if (selected.length < maxSelections) {
      onChange([...selected, descriptor]);
    }
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategory(expandedCategory === categoryName ? null : categoryName);
  };

  return (
    <div className="space-y-2">
      {categories.map((category) => (
        <div key={category.name} className="border border-dark-700 rounded-xl overflow-hidden">
          {/* Category Header */}
          <button
            onClick={() => toggleCategory(category.name)}
            className="w-full flex items-center justify-between p-3 bg-dark-800/50 hover:bg-dark-800 transition-colors"
          >
            <span className="font-medium text-dark-200">{category.name}</span>
            <div className="flex items-center gap-2">
              {category.descriptors.some((d) => selected.includes(d)) && (
                <span className="text-xs text-primary-400">
                  {category.descriptors.filter((d) => selected.includes(d)).length} ausgewählt
                </span>
              )}
              <ChevronDown
                className={clsx(
                  'w-5 h-5 text-dark-400 transition-transform',
                  expandedCategory === category.name && 'rotate-180'
                )}
              />
            </div>
          </button>

          {/* Category Descriptors */}
          <AnimatePresence>
            {expandedCategory === category.name && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="p-3 flex flex-wrap gap-2">
                  {category.descriptors.map((descriptor) => {
                    const isSelected = selected.includes(descriptor);
                    const isDisabled = !isSelected && selected.length >= maxSelections;

                    return (
                      <button
                        key={descriptor}
                        onClick={() => toggleDescriptor(descriptor)}
                        disabled={isDisabled}
                        className={clsx(
                          'px-3 py-1.5 rounded-full text-sm font-medium',
                          'transition-all duration-200',
                          'border',
                          isSelected
                            ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                            : isDisabled
                            ? 'border-dark-700 bg-dark-800/50 text-dark-600 cursor-not-allowed'
                            : 'border-dark-700 bg-dark-800 text-dark-300 hover:border-primary-500/50 hover:text-primary-400'
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                        {descriptor}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

      {/* Selected Summary */}
      {selected.length > 0 && (
        <div className="mt-4 p-3 bg-primary-500/10 border border-primary-500/30 rounded-xl">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-dark-300">Ausgewählt:</span>
            <span className="text-primary-400">{selected.length} / {maxSelections}</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {selected.map((descriptor) => (
              <button
                key={descriptor}
                onClick={() => toggleDescriptor(descriptor)}
                className="px-2 py-0.5 rounded-full text-xs bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 transition-colors"
              >
                {descriptor} ×
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
